<template>
  <div class="material-assistant-step5">
    <!-- Return to source button -->
    <div
      v-if="store.materialAssistantSourcePage"
      class="return-to-source-section"
    >
      <ReactiveButton
        :theme-color="'base'"
        @click="returnToSource"
      >
        <MdArrowForward class="return-arrow" />
        {{ returnButtonText }}
      </ReactiveButton>
    </div>

    <div class="summary-wrapper">
      <h3>{{ $tv('Frontend.Text.Summary', 'ORingConfiguratorImport') }}</h3>
      <div class="summary">
        <div>
          <span>{{ $tv('Frontend.Title.Media', 'ORingConfiguratorImport') }}</span>

          <strong>
            {{ store.oringMaterialAssistantFluidOptions
              .filter(f => store.oringMaterialAssistantFluidIds.includes(f.fluidId))
              .map(f => f.fluidName)
              .join(', ') }}
          </strong>
        </div>
        <div>
          <span>{{ $tv('Frontend.Title.Temperature', 'ORingConfiguratorImport') }}</span>
          <strong>{{ store.materialAssistantMinTemp }} – {{ store.materialAssistantMaxTemp }} {{ store.oringCalculatorFahrenheit ? $tv('fahrenheit','unitsOfMeasure') : $tv('celsius', 'unitsOfMeasure') }}</strong>
        </div>
        <div>
          <span>{{ $tv('Frontend.Parameter.PressureMax', 'ORingConfiguratorImport') }}</span>
          <strong>{{ Number(store.oringMaterialAssistantPressure).toFixed(1) }} {{ store.oringCalculatorPsi ? $tv('psi', 'unitsOfMeasure') : $tv('bar','unitsOfMeasure') }}</strong>
        </div>
        <div>
          <span>{{ $tv('Frontend.Parameter.GapWidthMax', 'ORingConfiguratorImport') }}</span>
          <strong>{{ store.oringMaterialAssistantGapWidth }} {{ store.oringCalculatorImperial ? $tv('inch', 'unitsOfMeasure') : $tv('mm', 'unitsOfMeasure') }}</strong>
        </div>
        <div>
          <span>{{ $tv('Frontend.Title.Movement', 'ORingConfiguratorImport') }}</span>
          <strong>{{ store.oringMaterialAssistantIsDynamic ? $tv('Frontend.Application.Dynamic', 'ORingConfiguratorImport') : $tv('Frontend.Application.Static', 'ORingConfiguratorImport') }}</strong>
        </div>
      </div>
    </div>
    <!-- Mobile materials list (shown below $lg) -->
    <div class="materials-list-container">
      <div
        v-if="loading"
        class="placeholder-content"
      >
        <AppLoadingIndicator />
      </div>
      <template v-else>
        <div
          v-if="results && results.length"
          class="materials-list"
        >
          <div class="mobile-results-header">
            <h3 class="mobile-results-title">
              {{ $tv('results', 'ORingConfigurator') }}
            </h3>
          </div>
          <div
            v-for="(item, idx) in results"
            :key="`list-${idx}`"
            class="materials-list__item card"
          >
            <div class="materials-list__name">
              {{ item.materialName }}
            </div>

            <!-- Show up to 3 compatible selected fluids -->
            <div
              v-for="fluid in getCompatibleSelectedFluids(item)"
              :key="`list-${idx}-${fluid.fluidId}`"
              class="materials-list__compatibility-line"
            >
              <CheckmarkFilled class="compatibility-check" />
              <span class="materials-list__fluid">{{ fluid.fluidName }}</span>
            </div>

            <div class="materials-list__actions">
              <a
                href="javascript:void(0)"
                class="materials-list__link"
                @click="toggleBookmark(item.materialId)"
              >
                <ActionBookmark v-if="isBookmarked(item.materialId)" />
                <ActionBookmarkBorder v-else />
                <span>{{ $tv('saveAsBookmark', 'ORingConfigurator') }}</span>
              </a>

              <a
                v-if="item.materialDataSheetUrl"
                href="javascript:void(0)"
                class="materials-list__link"
                @click="openDownloadSheetPopup(item)"
              >
                <Download />
                <span>{{ $tv('downloadDataSheet', 'ORingConfigurator') }}</span>
              </a>
            </div>
          </div>
        </div>

        <div
          v-else
          class="empty-state no-materials-found-centered"
        >
          <div class="no-materials-icon-wrapper">
            <NoProductsFound class="no-materials-icon" />
          </div>
          <div class="no-materials-message">
            {{ $tv('Frontend.Text.NoResults', 'ORingConfiguratorImport') }}
          </div>
        </div>
      </template>
    </div>

    <div class="oring-assistant__container__cards">
      <div class="card">
        <div class="card-content">
          <div class="card-content__title">
            <h4 class="h4 card-content__title-text">
              {{ $tv('results', 'ORingConfigurator') }}
            </h4>
          </div>

          <div
            v-if="loading"
            class="placeholder-content"
          >
            <AppLoadingIndicator />
          </div>
          <div v-else>
            <div
              v-if="results && results.length"
              class="materials-table-container"
            >
              <table class="materials-table">
                <thead>
                  <tr>
                    <th class="material-name-col">
                      {{ $tv('material', 'ORingConfigurator') }}
                    </th>
                    <th
                      v-for="fluid in selectedFluids"
                      :key="fluid.fluidId"
                      class="fluid-col"
                      :class="{ 'fluid-col-empty': fluid.isEmpty }"
                    >
                      <span v-if="!fluid.isEmpty">{{ fluid.fluidName }}</span>
                    </th>
                    <th class="action-col">
                      {{ $tv('saveAsBookmark', 'ORingConfigurator') }}
                    </th>
                    <th class="action-col">
                      {{ $tv('downloadDataSheet', 'ORingConfigurator') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in results"
                    :key="idx"
                    class="material-row"
                  >
                    <td class="material-name-col">
                      {{ item.materialName }}
                    </td>
                    <td
                      v-for="fluid in selectedFluids"
                      :key="`${item.materialId}-${fluid.fluidId}`"
                      class="compatibility-cell fluid-col"
                      :class="{ 'fluid-col-empty': fluid.isEmpty }"
                    >
                      <template v-if="!fluid.isEmpty">
                        <!-- Show checkmark only if this material is compatible with this fluid -->
                        <CheckmarkFilled
                          v-if="isFluidCompatible(item, fluid)"
                          class="compatibility-check"
                        />
                        <span v-else>&nbsp;</span>
                      </template>
                    </td>
                    <td class="action-cell action-col">
                      <button
                        type="button"
                        class="bookmark-btn"
                        @click="toggleBookmark(item.materialId)"
                      >
                        <ActionBookmark
                          v-if="isBookmarked(item.materialId)"
                        />
                        <ActionBookmarkBorder
                          v-else
                        />
                      </button>
                    </td>
                    <td class="action-cell action-col">
                      <button
                        v-if="item.materialDataSheetUrl"
                        type="button"
                        class="download-btn"
                        @click="openDownloadSheetPopup(item)"
                      >
                        <Download />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-else
              class="empty-state no-materials-found-centered"
            >
              <div class="no-materials-icon-wrapper">
                <NoProductsFound class="no-materials-icon" />
              </div>
              <div class="no-materials-message">
                {{ $tv('Frontend.Text.NoResults', 'ORingConfiguratorImport') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <OringLanguageDownloadSheet
      :show="showDownloadSheetPopup"
      :loading="downloadSheetLoading"
      :model-value="downloadSheetLanguage"
      @close-pop-up="closeDownloadSheetPopup"
      @download="(language) => handleDownloadSheet(language, downloadSheetMaterial?.materialId)"
    />
  </div>
</template>

<script setup lang="ts">
import {defineAsyncComponent} from 'vue'
import AppLoadingIndicator from '~/components/loading-indicators/app-loading-indicator.vue'
import ReactiveButton from '~/components/ReactiveButton.vue'
import NoProductsFound from '~/assets/icons/no_products_found.svg?component'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {useOringCalculatorService} from '@/composables/oring_calc_service'
import useI18nExtended from '~/composables/usei18nExtended'
import type {ORingMaterialAssistantSearchRequest, ORingSearchParameter} from '@/types/oring-api'
import ActionBookmark from '~/assets/icons/action_bookmark.svg?component'
import ActionBookmarkBorder from '~/assets/icons/action_bookmark_border.svg?component'
import Download from '~/assets/icons/download.svg?component'
import CheckmarkFilled from '~/assets/icons/checkmark_filled.svg?component'
import MdArrowForward from '~/assets/icons/md_arrow_forward.svg?component'
import {useGtm} from '@gtm-support/vue-gtm'
const OringLanguageDownloadSheet = defineAsyncComponent(() => import('~/components/popup/OringLanguageDownloadSheet.vue'))

const i18n = useI18nExtended()

// Popup state for language selection and download
const showDownloadSheetPopup = ref(false)
const downloadSheetLoading = ref(false)
const downloadSheetMaterial = ref<any>(null)
const downloadSheetLanguage = ref(i18n.locale.value ?? 'en')

function openDownloadSheetPopup(item: any) {
  downloadSheetMaterial.value = item
  showDownloadSheetPopup.value = true
  downloadSheetLanguage.value = i18n.locale.value
}

function closeDownloadSheetPopup() {
  showDownloadSheetPopup.value = false
  downloadSheetLoading.value = false
  downloadSheetMaterial.value = null
}

async function handleDownloadSheet(language: {value: string, label: string}, materialId: number) {
  if (!downloadSheetMaterial.value?.materialDataSheetUrl) {return}
  downloadSheetLoading.value = true

  try {
    let materialDataSheetUrl = downloadSheetMaterial.value.materialDataSheetUrl

    // If language is different from current locale, make a new search call
    if (language.value !== i18n.locale.value) {
      const searchParameters: ORingSearchParameter[] = [
        {searchParameterKey: 'Pressure', searchValue: String(store.oringMaterialAssistantPressure)},
        {searchParameterKey: 'GapWidth', searchValue: String(store.oringMaterialAssistantGapWidth)},
        {searchParameterKey: 'MinTemperature', searchValue: String(store.materialAssistantMinTemp)},
        {searchParameterKey: 'MaxTemperature', searchValue: String(store.materialAssistantMaxTemp)},
        {searchParameterKey: 'Movement', searchValue: (store.oringMaterialAssistantIsDynamic ? 'dynamic' : 'static')}
      ]

      // Add each fluid as a separate parameter object
      if (store.oringMaterialAssistantFluidIds.length > 0) {
        store.oringMaterialAssistantFluidIds.forEach(fluidId => {
          searchParameters.push({
            searchParameterKey: 'Fluids',
            searchValue: String(fluidId)
          })
        })
      }

      const request: ORingMaterialAssistantSearchRequest = {
        searchParameters,
        imperial: store.oringCalculatorImperial,
        language: language.value, // Use the selected language instead of current locale
        fahrenheit: store.oringCalculatorFahrenheit,
        savedMaterialId: null
      }

      const res = await searchMaterialAssistant(request)
      const materials = Array.isArray(res?.data?.materials) ? res.data.materials : []

      // Find the material with the same materialId
      const material = materials.find((m: any) => m.materialId === materialId)
      if (material?.materialDataSheetUrl) {
        materialDataSheetUrl = material.materialDataSheetUrl
      }
    }

    // Open the URL
    window.open(materialDataSheetUrl, '_blank')

    useGtm()?.trackEvent({event: 'oring_downloadmaterialdatasheet'})

    closeDownloadSheetPopup()
  } catch {
    // eslint-disable-next-line no-console
    console.error('Error downloading data sheet')
  } finally {
    downloadSheetLoading.value = false
  }
}

// Constants for source page values
const SOURCE_PAGE = {
  DESIGN_COCKPIT: 'design-cockpit',
  ORING_ASSISTANT: 'oring-assistant'
} as const

// Constants for navigation paths
const NAVIGATION_PATHS = {
  DESIGN_COCKPIT: '/oring-calculator/design-cockpit',
  ORING_ASSISTANT: '/oring-calculator/oring-assistant'
} as const

const store = useORingCalculatorStore()
const {searchMaterialAssistant} = useOringCalculatorService()
const app = useNuxtApp()
const {tv: $tv} = useI18nExtended()

const results = ref<any[]>([])
const loading = ref(true)
const compatibilityMap = ref<Map<string, boolean>>(new Map())

// Get selected fluids from store
type FluidOption = {
  fluidId: number
  fluidName: string
  isEmpty?: boolean
}

const selectedFluids = computed<FluidOption[]>(() => {
  const selected: FluidOption[] = store.oringMaterialAssistantFluidOptions
    .filter(f => store.oringMaterialAssistantFluidIds.includes(f.fluidId))
  while (selected.length < 3) {
    selected.push({fluidId: -1, fluidName: '', isEmpty: true})
  }
  return selected
})

// Build compatibility map when results change
function buildCompatibilityMap() {
  const map = new Map<string, boolean>()

  // For each result item, mark only the materialId-fluidName pairs from API response as compatible
  results.value.forEach(result => {
    if (result.fluidsWithRatings && Array.isArray(result.fluidsWithRatings)) {
      result.fluidsWithRatings.forEach((fluidRating: any) => {
        // Prefer fluidId if provided by API; fall back to fluidName to preserve legacy behavior
        const fluidKey = fluidRating.fluidId ?? fluidRating.fluidName
        const key = `${result.materialId}-${fluidKey}`
        map.set(key, true)
      })
    }
  })

  compatibilityMap.value = map
}

function isFluidCompatible(material: any, fluid: any) {
  // Use id-based lookup primarily; name as a safe fallback
  const keyById = `${material.materialId}-${fluid.fluidId}`
  if (compatibilityMap.value.has(keyById)) {return true}

  const keyByName = `${material.materialId}-${fluid.fluidName}`
  return compatibilityMap.value.get(keyByName) || false
}

function isBookmarked(materialId: number) {
  return store.oringBookmarkedMaterialIds.includes(materialId)
}

function toggleBookmark(materialId: number) {
  const index = store.oringBookmarkedMaterialIds.indexOf(materialId)
  if (index > -1) {
    // Remove from bookmarks
    store.oringBookmarkedMaterialIds.splice(index, 1)
  } else {
    // Add to bookmarks
    store.oringBookmarkedMaterialIds.push(materialId)
  }
}

// Helper: get only selected fluids that are compatible with a given material
function getCompatibleSelectedFluids(item: any) {
  return selectedFluids.value
    .filter(f => !f.isEmpty && isFluidCompatible(item, f))
    .slice(0, 3)
}

async function fetchResults() {
  loading.value = true

  try {
    const searchParameters: ORingSearchParameter[] = [
      {searchParameterKey: 'Pressure', searchValue: String(store.oringMaterialAssistantPressure)},
      {searchParameterKey: 'GapWidth', searchValue: String(store.oringMaterialAssistantGapWidth)},
      {searchParameterKey: 'MinTemperature', searchValue: String(store.materialAssistantMinTemp)},
      {searchParameterKey: 'MaxTemperature', searchValue: String(store.materialAssistantMaxTemp)},
      {searchParameterKey: 'Movement', searchValue: (store.oringMaterialAssistantIsDynamic ? 'dynamic' : 'static')}
    ]
    // Add each fluid as a separate parameter object
    if (store.oringMaterialAssistantFluidIds.length > 0) {
      store.oringMaterialAssistantFluidIds.forEach(fluidId => {
        searchParameters.push({
          searchParameterKey: 'Fluids',
          searchValue: String(fluidId)
        })
      })
    }

    const request: ORingMaterialAssistantSearchRequest = {
      searchParameters,
      imperial: store.oringCalculatorImperial,
      language: i18n.locale.value,
      fahrenheit: store.oringCalculatorFahrenheit,
      savedMaterialId: null
    }
    const res = await searchMaterialAssistant(request)
    results.value = Array.isArray(res?.data?.materials) ? res.data.materials : []
    buildCompatibilityMap() // Build the compatibility map after getting results
  } catch {
    results.value = []
    buildCompatibilityMap() // Clear the map on error
  }
  loading.value = false
}

onMounted(fetchResults)

// Refetch fluid options and results when language changes
watch(() => i18n.locale.value, async () => {
  await store.loadOringFluidOptions()
  fetchResults()
})

// Computed property for return button text
const returnButtonText = computed(() => {
  if (store.materialAssistantSourcePage === SOURCE_PAGE.DESIGN_COCKPIT) {
    return $tv('backToDesignCockpit', 'ORingConfigurator')
  } else if (store.materialAssistantSourcePage === SOURCE_PAGE.ORING_ASSISTANT) {
    return $tv('backToAssistant', 'ORingConfigurator')
  }
  return ''
})

// Function to return to source page
function returnToSource() {
  if (store.materialAssistantSourcePage === SOURCE_PAGE.DESIGN_COCKPIT) {
    const path = app.$globalization.localePath(NAVIGATION_PATHS.DESIGN_COCKPIT, i18n.locale.value, app.$globalization.getRegion())
    navigateTo(path)
  } else if (store.materialAssistantSourcePage === SOURCE_PAGE.ORING_ASSISTANT) {
    const path = app.$globalization.localePath(NAVIGATION_PATHS.ORING_ASSISTANT, i18n.locale.value, app.$globalization.getRegion())
    navigateTo(path)
  }
}

function validate() {
  return true
}
defineExpose({validate})
</script>

<style lang="scss">
.placeholder-content {
  width: 100%;
  min-height: 18.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-materials-found-centered {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  width: 100%;
}

.no-materials-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 1.5rem;

  .icon--svg {
    width: 9rem;
    height: 9rem;
    color: $color__fst__primary;
  }
}

.no-materials-message {
  font-size: 1.25rem;
  text-align: center;
  margin-top: 0.5rem;
  font-weight: $base-text-font-weight-bold;
}

.material-assistant-step5 {
  .material-name-col {
    font-weight: $base-text-font-weight-light;
  }

  .summary-wrapper {
    margin-top: 1rem;
    background-color: $color__content--background__secondary;
    padding: 1rem;

    h3 {
      margin: 0 0 1rem;
    }
  }

  .summary {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 1rem;

    @include breakpoint-down($lg) {
      flex-direction: column;
      gap: 0;

      /* Add double grey divider between rows (not the last) only below $lg */
      > div:not(:last-child) {
        border-bottom: .125rem double $color__fst__gray__020;
        padding-bottom: .75rem;
        margin-bottom: .75rem;
      }
    }

    div {
      display: flex;
      flex-direction: column;
      font-weight: $base-text-font-weight-light;

      strong {
        margin-top: .5rem;
        font-weight: $base-text-font-weight-bold;
      }
    }

    /* Mobile: place label (span) and value (strong) side-by-side with equal label width */
    @include breakpoint-down($md) {
      div {
        flex-direction: row;
        align-items: baseline;
        gap: .5rem;

        span {
          flex: 0 0 50%;
          display: inline-block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        strong {
          flex: 1 1 50%;
          margin-top: 0;
        }
      }
    }
  }

  .oring-assistant__container__cards {
    margin-bottom: 1rem;

    .card {
      padding-bottom: 0;
    }
  }

  /* Mobile materials list visibility and styles */
  .materials-list-container {
    display: none;

    @include breakpoint-down($md) {
      display: block;
      margin: 0;
    }
  }

  /* Mobile-only results header */
  .mobile-results-header {
    display: none;

    @include breakpoint-down($md) {
      display: block;
  margin: .5rem 0 0;
    }

    .mobile-results-title {
      margin: 1rem 0 0;
      color: $color__primary;
    }
  }

  @include breakpoint-down($md) {
    .oring-assistant__container__cards {
      display: none;
    }
  }

  .materials-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .materials-list__item {
    background: $color__white;
    padding: 1rem;
    box-shadow: $shadow-default;

    /* Ensure no external .card margin leaks in */
    &.card { margin: 0; }
  }

  /* Fallback when flex gap is not supported */
  @supports not (gap: 1rem) {
    .materials-list { gap: 0; }
    .materials-list__item + .materials-list__item { margin-top: 1rem; }
  }

  .materials-list__name {
    font-weight: $base-text-font-weight-bold;
    font-size: 1rem;
    color: $color__primary;
    margin-bottom: .5rem;
  }

  .materials-list__compatibility-line {
    display: flex;
    align-items: center;
    gap: 1rem;
    line-height: 1.5rem;

    .compatibility-check {
      color: $color__fst__success;
      width: 1.25rem;
      height: 1.25rem;
      flex: 0 0 1.25rem;
    }
  }

  .materials-list__fluid {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: $base-text-font-weight-light;
  }

  .materials-list__actions {
    margin-top: .75rem;
    display: flex;
    flex-direction: column;
    gap: .25rem;
  }

  /* Keep default link color; only control layout/spacing and weight */
  .materials-list__link {
    display: inline-flex;
    align-items: center;
    gap: 1rem;

    span {
      font-weight: $base-text-font-weight-semi-bold;
    }

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

  .materials-table-container {
    margin-bottom: 2rem;
    min-height: 18.5rem;
    max-height: 18.5rem;
    border-bottom: .0625rem solid $color__primary;
    overflow-y: scroll;
    position: relative;

    /* Custom scrollbar styling for container */
    &::-webkit-scrollbar {
      position: absolute;
      top: 2rem;
      width: .625rem;
    }

    &::-webkit-scrollbar-track {
      background-color: $color__page--background;
    }

    &::-webkit-scrollbar-thumb {
      background: $color__primary;
      min-height: 5rem;
    }

    scrollbar-width: .3rem;

      .empty-state {
        text-align: center;
        padding: 3rem;
        font-style: italic;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        .empty-content {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;

          svg {
            font-size: 7.25rem;
            color: $color__default;
            padding-right: .5rem;
          }
        }
      }

    .materials-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }

    thead {
      color: $color__primary;
      font-size: .875rem;
      background: $color__white;
      position: sticky;
      top: 0;
      z-index: 10;
      border-bottom: .0625rem solid $color__primary;

      tr th {
        font-weight: $base-text-font-weight-light;
        height: 2rem;
      }
    }

    .fluid-col {
      align-items: center;
    }

    td, th {
      padding: 0 .5rem;
      text-align: left;
      line-height: 2.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    td {
      border-bottom: .125rem solid $color__fst__gray__020;
    }

    .compatibility-cell {
      text-align: left;
      vertical-align: middle;

      .compatibility-check {
        color: $color__fst__success;
        width: 1.25rem;
        height: 1.25rem;
        vertical-align: middle;
      }
    }

    .action-col {
      text-align: left;
    }

    .action-cell {
      text-align: left;
      padding: 0.25rem;
      vertical-align: middle;

      button {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.25rem;

        svg {
          width: 1.25rem;
          height: 1.25rem;
        }
      }

      .bookmark-btn svg {
        vertical-align: middle;
        color: $color__primary;
      }

      .download-btn svg {
        vertical-align: middle;
        color: $color__primary;
      }
    }
  }
}

.fluid-col-empty {
  background: transparent !important;
  pointer-events: none;
  border: none;
  box-shadow: none;

  // Hide on small screens
  @include breakpoint-down($md) {
    display: none !important;
  }
}

.return-to-source-section {
  margin: 1rem 0 0;

  .return-arrow {
    transform: rotate(180deg);
    height: 1.5rem;
    width: 1.5rem;
    margin-right: 0.5rem;
  }

  .k-button-text {
    align-items: center;
  }
}
</style>
